import React from "react";
import { useState, useEffect } from "react";
import "../styles/navbar.css";
import { forwardRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import logoImage from "../assets/motola-logo-last3.svg";

export const NavBar = forwardRef((props, refs) => {
  const [activeLink, setActiveLink] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const homeRef = refs?.homeRef;
  const skillsRef = refs?.skillsRef;
  const projectsRef = refs?.projectsRef;
  const contactsRef = refs?.contactsRef;

  useEffect(() => {
    const sectionRefs = [
      { key: "home", ref: homeRef },
      { key: "skills", ref: skillsRef },
      { key: "projects", ref: projectsRef },
      { key: "contacts", ref: contactsRef },
    ];

    const getActiveSection = () => {
      const scrollAnchor = window.scrollY + 160;
      let nextActive = "home";

      for (const section of sectionRefs) {
        const sectionElement = section.ref?.current;

        if (!sectionElement) {
          continue;
        }

        if (scrollAnchor >= sectionElement.offsetTop) {
          nextActive = section.key;
        }
      }

      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 4
      ) {
        return sectionRefs[sectionRefs.length - 1]?.key || nextActive;
      }

      return nextActive;
    };

    const onScroll = () => {
      const nextScrolled = window.scrollY > 150;
      setScrolled((prevScrolled) =>
        prevScrolled === nextScrolled ? prevScrolled : nextScrolled
      );

      const nextActive = getActiveSection();
      setActiveLink((prevActive) =>
        prevActive === nextActive ? prevActive : nextActive
      );
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [contactsRef, homeRef, projectsRef, skillsRef]);

  const scrollToSection = (targetRef) => {
    setActiveLink(targetRef.current.id);
    setIsNavExpanded(false);
    targetRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleNavLinkClick = (event, targetRef) => {
    event.preventDefault();
    scrollToSection(targetRef);
  };

  return (
    <nav className={scrolled ? "scrolled" : ""}>
      <Container fluid className="navbar__fluid px-lg-0 px-2">
        <Row className="justify-content-center navbar__row">
          <Col lg={10} className="navbar__col">
            <div
              className={
                isNavExpanded
                  ? "navbar__inner px-lg-3 px-2 navbar__inner--expanded"
                  : "navbar__inner px-lg-3 px-2"
              }
            >
              <div className="logo-holder logo_container">
                <a
                  href="#home"
                  className="header_link"
                  onClick={(event) => handleNavLinkClick(event, homeRef)}
                >
                  <img
                    src={logoImage}
                    alt="Ron Motola Web Developer"
                    className="logo_image"
                    loading="eager"
                    decoding="async"
                  />
                </a>
              </div>
              <button
                className="hamburger"
                onClick={() => {
                  setIsNavExpanded(!isNavExpanded);
                }}
                type="button"
                aria-expanded={isNavExpanded}
                aria-controls="main-navigation"
                aria-label={
                  isNavExpanded ? "Close navigation menu" : "Open navigation menu"
                }
              >
                <span className="navbar-toggler-icon" aria-hidden="true"></span>
              </button>
              <ul
                id="main-navigation"
                className={isNavExpanded ? "nav-links open" : "nav-links"}
              >
                <li className={isNavExpanded ? "fadelinks" : ""}>
                  <a
                    href="#home"
                    className={
                      activeLink === "home"
                        ? "active navbar-link"
                        : "navbar-link"
                    }
                    aria-current={activeLink === "home" ? "page" : undefined}
                    onClick={(event) => handleNavLinkClick(event, homeRef)}
                  >
                    Home
                  </a>
                </li>
                <li className={isNavExpanded ? "fadelinks" : ""}>
                  <a
                    href="#skills"
                    className={
                      activeLink === "skills" ? "active navbar-link" : "navbar-link"
                    }
                    aria-current={activeLink === "skills" ? "page" : undefined}
                    onClick={(event) => handleNavLinkClick(event, skillsRef)}
                  >
                    Skills
                  </a>
                </li>
                <li className={isNavExpanded ? "fadelinks" : ""}>
                  <a
                    href="#projects"
                    className={
                      activeLink === "projects"
                        ? "active navbar-link"
                        : "navbar-link"
                    }
                    aria-current={activeLink === "projects" ? "page" : undefined}
                    onClick={(event) =>
                      handleNavLinkClick(event, projectsRef)
                    }
                  >
                    Projects
                  </a>
                </li>
                <li className={isNavExpanded ? "fadelinks" : ""}>
                  <a
                    href="#contacts"
                    className={
                      activeLink === "contacts"
                        ? "active navbar-link"
                        : "navbar-link"
                    }
                    aria-current={activeLink === "contacts" ? "page" : undefined}
                    onClick={(event) =>
                      handleNavLinkClick(event, contactsRef)
                    }
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      </Container>
    </nav>
  );
});
